/**
 * The Layers panel — `docs/property-maps-v2.md` §5.3, board 2.
 *
 * One row per layer: the eye, the colour swatch, the name, the audience chip,
 * the shape count, and a `⋯`. The **active** layer — the one drawing goes on
 * — wears a ring and expands to list its shapes.
 *
 * **The eye hides a layer on your screen only.** Client state, remembered per
 * map, never a write, never a change to sharing — which is why the footer
 * says where sharing *is* decided. A control that looks like "stop the crews
 * seeing this" and only dims your own canvas is worse than no control.
 *
 * **Deleting a layer always confirms**, names the shape count, and offers to
 * move the shapes instead (§5.7). It is the one change on this screen that
 * Undo cannot take back, and `use-map-editing.ts` says why.
 */
import { useState, type ReactNode } from 'react'
import { LAYER_PALETTE, formatArea, formatLength } from '@rowted/shared'
import type { Audience, LayerColor } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { ContextMenu } from '../../components/context-menu.js'
import { ConfirmDialog } from '../../components/overlays.js'
import { SelectInput } from '../../components/form/inputs.js'
import { IconChevronRight, IconEye, IconMore } from '../../frame/icons.js'
import { duplicateLayer, type MapFeature, type MapLayer } from '../api.js'
import { shapeWords } from '../words.js'
import { AudienceChip, type Division } from '../sharing/audience-chip.js'

export interface LayerPatch {
  name?: string
  color?: LayerColor
  audience?: Audience
  divisionIds?: string[]
  pinnedToTickets?: boolean
  crewCanAdd?: boolean
  hiddenOnTickets?: boolean
}

export function LayersPanel({
  layers, features, divisions, hidden, activeId, selectedId, canEdit, phone,
  onToggle, onActivate, onSelectShape, onChangeLayer, onDeleteLayer, onDuplicate, onShare,
  onOpenLayer, footer,
}: {
  layers: MapLayer[]
  features: MapFeature[]
  divisions: Division[]
  hidden: ReadonlySet<string>
  activeId?: string | null
  selectedId?: string | null
  canEdit?: boolean
  onToggle: (layerId: string) => void
  onActivate?: (layerId: string) => void
  onSelectShape?: (featureId: string) => void
  onChangeLayer?: (layerId: string, patch: LayerPatch) => void
  onDeleteLayer?: (layerId: string, moveTo: string | null) => void
  onDuplicate?: () => void
  onShare?: (layer: MapLayer) => void
  /**
   * §6.4: on a phone the whole row opens Layer options, so there is no `⋯`.
   *
   * A context menu is the right shape for a pointer and the wrong one for a
   * sheet — it opens at a touch coordinate, which arrives as a floating card
   * over a panel that came up from the bottom. Two shapes behind one control.
   */
  phone?: boolean
  onOpenLayer?: (layer: MapLayer) => void
  footer?: ReactNode
}) {
  const ordered = [...layers].sort((a, b) => a.sortOrder - b.sortOrder)
  const [menu, setMenu] = useState<{ layer: MapLayer; x: number; y: number } | null>(null)
  const [renaming, setRenaming] = useState<MapLayer | null>(null)
  const [deleting, setDeleting] = useState<MapLayer | null>(null)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {ordered.length === 0
          ? (
            <p className="m-0 px-3 py-4 text-cell text-text-muted">
              This map has no layers yet.
            </p>
          )
          : ordered.map((layer) => {
            const mine = features.filter((one) => one.layerId === layer.id)
            const off = hidden.has(layer.id)
            const active = layer.id === activeId
            return (
              <div key={layer.id}>
                <div
                  className={cn(
                    'flex items-center gap-2 border-b border-line-faint pl-3 pr-2.5',
                    phone ? 'min-h-14 py-2 text-body' : 'py-[9px] text-cell',
                    active && 'bg-accent-soft/40 ring-1 ring-inset ring-accent-line',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onToggle(layer.id)}
                    aria-pressed={!off}
                    title={off
                      ? `Show ${layer.name} on your screen`
                      : `Hide ${layer.name} on your screen`}
                    className={cn(
                      'inline-flex shrink-0 items-center justify-center rounded-tile',
                      /* §6.4 asks for a 44px target: the glyph stays the
                         size it is and the box around it grows. */
                      phone ? 'h-11 w-11' : 'h-6 w-6',
                      off ? 'text-line-strong' : 'text-text-body',
                      'hover:bg-hover',
                    )}
                  >
                    <IconEye size={phone ? 18 : 15} />
                  </button>

                  <span
                    aria-hidden
                    className="h-3 w-3 shrink-0 rounded-tile-small"
                    style={{ background: LAYER_PALETTE[layer.color] }}
                  />

                  <button
                    type="button"
                    onClick={() => onActivate?.(layer.id)}
                    disabled={!onActivate || off}
                    className={cn(
                      'flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left',
                      /* §7: the row is 56px and this is what you tap to make
                         the layer active, so it fills the row rather than
                         sitting 35px tall in the middle of it. */
                      phone && 'min-h-11 justify-center',
                    )}
                  >
                    <b className="w-full truncate font-semibold">{layer.name}</b>
                    <AudienceChip
                      audience={layer.audience}
                      divisionIds={layer.divisionIds}
                      divisions={divisions}
                      className="min-w-0 max-w-full truncate text-sub"
                    />
                  </button>

                  <span className="shrink-0 text-sub text-text-faint">
                    {shapeWords(mine.length)}
                  </span>

                  {canEdit && (phone
                    ? (
                      <button
                        type="button"
                        aria-label={`${layer.name} options`}
                        onClick={() => onOpenLayer?.(layer)}
                        className="flex h-11 w-11 shrink-0 items-center justify-end text-text-faint"
                      >
                        <IconChevronRight size={17} />
                      </button>
                    )
                    : (
                      <button
                        type="button"
                        aria-label={`${layer.name} options`}
                        onClick={(event) =>
                          setMenu({ layer, x: event.clientX, y: event.clientY })}
                        className="shrink-0 text-text-faint hover:text-text"
                      >
                        <IconMore size={15} />
                      </button>
                    ))}
                </div>

                {/* The active layer lists its shapes (§5.3). */}
                {active && mine.length > 0 && (
                  <ul className="m-0 list-none border-b border-line-faint bg-surface-alt p-0">
                    {mine.map((shape) => (
                      <li key={shape.id}>
                        <button
                          type="button"
                          onClick={() => onSelectShape?.(shape.id)}
                          className={cn(
                            'flex w-full items-center gap-2 pr-3 text-left hover:bg-hover',
                            phone
                              ? 'min-h-11 py-2 pl-[62px] text-cell'
                              : 'py-1.5 pl-[46px] text-sub',
                            shape.id === selectedId && 'bg-accent-soft',
                          )}
                        >
                          <span className="min-w-0 flex-1 truncate">
                            {shape.name || unnamed(shape.kind)}
                          </span>
                          <span className="shrink-0 tabular-nums text-text-faint">
                            {measureWords(shape)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
      </div>

      {footer}

      {/*
        * §6.4 gives the sheet a shorter footnote than §5.3's panel, and the
        * difference is the point: on a phone the next thing to do is *tap a
        * layer*, and the paragraph about office users and the portal is a
        * paragraph nobody reads standing in a yard.
        */}
      <div className={cn(
        'mt-auto border-t border-line-faint px-3.5 py-2.5 text-[11.5px] leading-[1.45]',
        'text-text-muted',
      )}
      >
        {phone
          ? (
            <>
              <b className="text-text-body">The eye hides a layer on your screen only.</b>
              {' Sharing is set per layer — tap one, then Who sees it.'}
            </>
          )
          : (
            <>
              <b className="text-text-body">Who sees a layer is set per layer.</b>
              {' New layers start as All crews. Office users with Properties access see every'}
              {' shared layer. Nothing here ever shows in the customer portal.'}
            </>
          )}
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            { key: 'rename', label: 'Rename', onSelect: () => { setRenaming(menu.layer); setMenu(null) } },
            { key: 'share', label: 'Who sees it…', onSelect: () => { onShare?.(menu.layer); setMenu(null) } },
            {
              key: 'pin',
              label: menu.layer.pinnedToTickets ? 'Unpin from visits here' : 'Pin to visits here',
              onSelect: () => {
                onChangeLayer?.(menu.layer.id, { pinnedToTickets: !menu.layer.pinnedToTickets })
                setMenu(null)
              },
            },
            {
              key: 'duplicate',
              label: 'Duplicate layer',
              onSelect: () => {
                void duplicateLayer(menu.layer.id).then(() => onDuplicate?.())
                setMenu(null)
              },
            },
            {
              key: 'hide',
              label: menu.layer.hiddenOnTickets
                ? 'Show on visits again'
                : 'Hide on visits for now',
              onSelect: () => {
                onChangeLayer?.(menu.layer.id, { hiddenOnTickets: !menu.layer.hiddenOnTickets })
                setMenu(null)
              },
            },
            {
              key: 'delete',
              label: 'Delete layer…',
              tone: 'danger',
              separatorBefore: true,
              onSelect: () => { setDeleting(menu.layer); setMenu(null) },
            },
          ]}
        />
      )}

      {renaming && (
        <RenameLayer
          layer={renaming}
          onCancel={() => setRenaming(null)}
          onSave={(name) => { onChangeLayer?.(renaming.id, { name }); setRenaming(null) }}
        />
      )}

      {deleting && (
        <DeleteLayer
          layer={deleting}
          shapes={features.filter((one) => one.layerId === deleting.id).length}
          others={layers.filter((one) => one.id !== deleting.id)}
          onCancel={() => setDeleting(null)}
          onConfirm={(moveTo) => { onDeleteLayer?.(deleting.id, moveTo); setDeleting(null) }}
        />
      )}
    </div>
  )
}

function RenameLayer({ layer, onCancel, onSave }: {
  layer: MapLayer
  onCancel: () => void
  onSave: (name: string) => void
}) {
  const [name, setName] = useState(layer.name)
  return (
    <ConfirmDialog
      title={`Rename “${layer.name}”`}
      tone="default"
      confirmLabel="Rename"
      busyLabel="Renaming…"
      disabled={name.trim().length === 0}
      body={(
        <input
          value={name}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          className={cn(
            'h-9 w-full rounded-field border border-line bg-surface px-2.5 text-body',
            'outline-none focus:border-accent-line',
          )}
        />
      )}
      onCancel={onCancel}
      onConfirm={() => onSave(name.trim())}
    />
  )
}

/**
 * §5.7's delete confirm: the count, what happens to measurements, and the
 * offer to move the shapes somewhere rather than lose them.
 */
export function DeleteLayer({ layer, shapes, others, onCancel, onConfirm }: {
  layer: MapLayer
  shapes: number
  others: MapLayer[]
  onCancel: () => void
  onConfirm: (moveTo: string | null) => void
}) {
  const [moveTo, setMoveTo] = useState<string>('')
  return (
    <ConfirmDialog
      title={`Delete “${layer.name}”?`}
      confirmLabel={moveTo ? 'Move shapes and delete' : 'Delete layer'}
      busyLabel="Deleting…"
      body={(
        <div className="flex flex-col gap-3">
          <p className="m-0">
            {shapes === 0
              ? 'It has no shapes on it.'
              : `${shapes} ${shapes === 1 ? 'shape goes' : 'shapes go'} with it.`}
            {' Measurements already saved to the property stay, but lose their link to'}
            {' the map.'}
          </p>
          {shapes > 0 && others.length > 0 && (
            <SelectInput
              label="Move the shapes to another layer instead"
              value={moveTo}
              onChange={(value) => setMoveTo(value ?? "")}
              options={[
                { value: '', label: 'Delete them with the layer' },
                ...others.map((one) => ({ value: one.id, label: one.name })),
              ]}
            />
          )}
          <p className="m-0 text-cell text-text-muted">
            This one cannot be undone from the rail — nothing is deleted anywhere else.
          </p>
        </div>
      )}
      onCancel={onCancel}
      onConfirm={() => onConfirm(moveTo || null)}
    />
  )
}

const unnamed = (kind: string): string => ({
  area: 'Area', rect: 'Rectangle', circle: 'Circle', line: 'Line',
  marker: 'Marker', label: 'Label',
}[kind] ?? 'Shape')

/**
 * `3,980 sq ft`, `210 ft`, or nothing for a marker.
 *
 * Exported because the crew's shape card says the same thing about the same
 * shape, and a second copy of this is how one screen comes to read `3,980 sq
 * ft` while another reads `0.09 ac` for the one rectangle.
 */
export function measureWords(shape: MapFeature): string {
  if (shape.areaSqft !== null && shape.areaSqft > 0) return formatArea(shape.areaSqft, 'ft')
  if (shape.lengthFt !== null && shape.lengthFt > 0) return formatLength(shape.lengthFt)
  return ''
}
